/**
 * Date display driven by the platform settings (`platform.timezone` and the
 * Luxon-style `platform.dateFormat`, e.g. `dd/LL/yyyy`). Everything a list
 * shows goes through here so an admin in Darwin and a server in UTC agree on
 * what day a document was created.
 *
 * Tokens follow Luxon because that is what the API's formatter speaks; only
 * the subset that appears in real settings is mapped (`yyyy yy LLLL LLL LL L
 * MMMM MMM MM M dd d HH H hh h mm m ss s a`). Text in single quotes is passed
 * through verbatim; anything unrecognised is echoed as-is.
 */
export interface DateSettings {
  /** IANA zone; omitted or invalid = the browser's own. */
  timezone?: string;
  /** Luxon-style pattern for the date part. */
  dateFormat: string;
}

export const DEFAULT_DATE_FORMAT = 'dd/LL/yyyy';

type Parts = Record<string, string>;

const TOKEN_RE = /'[^']*'|yyyy|yy|LLLL|LLL|LL|L|MMMM|MMM|MM|M|dd|d|HH|H|hh|h|mm|m|ss|s|a|./g;

function partsOf(date: Date, timezone: string | undefined): Parts {
  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    timeZone: timezone,
  };
  let dtf: Intl.DateTimeFormat;
  try {
    dtf = new Intl.DateTimeFormat('en-US', opts);
  } catch {
    // Unknown zone in settings — fall back to the browser's rather than crash a list.
    dtf = new Intl.DateTimeFormat('en-US', { ...opts, timeZone: undefined });
  }
  const out: Parts = {};
  for (const p of dtf.formatToParts(date)) out[p.type] = p.value;
  // Month names come from a second pass — asking for them in the same call
  // would turn the numeric month into text.
  const monthNames = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    timeZone: dtf.resolvedOptions().timeZone,
  }).formatToParts(date);
  out.monthLong = monthNames.find((p) => p.type === 'month')?.value ?? '';
  return out;
}

function render(token: string, p: Parts): string {
  const hour = Number(p.hour);
  switch (token) {
    case 'yyyy':
      return p.year;
    case 'yy':
      return p.year.slice(-2);
    case 'LLLL':
    case 'MMMM':
      return p.monthLong;
    case 'LLL':
    case 'MMM':
      return p.monthLong.slice(0, 3);
    case 'LL':
    case 'MM':
      return p.month;
    case 'L':
    case 'M':
      return String(Number(p.month));
    case 'dd':
      return p.day;
    case 'd':
      return String(Number(p.day));
    case 'HH':
      return p.hour;
    case 'H':
      return String(hour);
    case 'hh':
      return String(hour % 12 || 12).padStart(2, '0');
    case 'h':
      return String(hour % 12 || 12);
    case 'mm':
      return p.minute;
    case 'm':
      return String(Number(p.minute));
    case 'ss':
      return p.second;
    case 's':
      return String(Number(p.second));
    case 'a':
      return hour < 12 ? 'AM' : 'PM';
    default:
      if (token.length > 1 && token.startsWith("'")) return token.slice(1, -1);
      return token;
  }
}

/** Format an instant with a Luxon-style pattern in the given zone. */
export function formatWithPattern(
  value: string | number | Date,
  pattern: string,
  timezone?: string,
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const p = partsOf(date, timezone);
  return (pattern.match(TOKEN_RE) ?? []).map((t) => render(t, p)).join('');
}

/** Does the pattern already carry a time-of-day? */
const hasTime = (pattern: string) => /HH|H|hh|h|mm|ss/.test(pattern.replace(/'[^']*'/g, ''));

/** The date part only, e.g. `03/09/2026`. */
export function formatDate(value: string | number | Date, settings: DateSettings): string {
  return formatWithPattern(value, settings.dateFormat || DEFAULT_DATE_FORMAT, settings.timezone);
}

/** Date + `HH:mm`, unless the configured pattern already has a time. */
export function formatDateTime(value: string | number | Date, settings: DateSettings): string {
  const base = settings.dateFormat || DEFAULT_DATE_FORMAT;
  return formatWithPattern(value, hasTime(base) ? base : `${base} HH:mm`, settings.timezone);
}

/** Date + `HH:mm:ss` — for audit trails, where seconds matter. */
export function formatTimestamp(value: string | number | Date, settings: DateSettings): string {
  const base = settings.dateFormat || DEFAULT_DATE_FORMAT;
  return formatWithPattern(value, hasTime(base) ? base : `${base} HH:mm:ss`, settings.timezone);
}

/** Time of day only, `HH:mm:ss`. */
export function formatTime(value: string | number | Date, settings: DateSettings): string {
  return formatWithPattern(value, 'HH:mm:ss', settings.timezone);
}
